/**
 * Products Repository (Phase 5g).
 * Phase B: upsertProduct via field write.
 * Phase C: upsertProduct uses single-row entity put under NO_KV_BLOB.
 */

import {
  queueBusinessPayloadFieldWrite,
  queueEntityItemUpsert
} from '../storage/businessPayloadWrite';
import { publishDomainEvent, DOMAIN_EVENTS } from '../events/domainEventBus';
import {
  readProductsFromBestSource,
  refreshProductsReadCache
} from '../../utils/productsReadSource';

function productEntityId(product) {
  if (!product || typeof product !== 'object') return '';
  if (product.id != null && product.id !== '') return String(product.id);
  if (product.productId != null && product.productId !== '') return String(product.productId);
  return '';
}

async function mergeProductIntoCollection(businessId, product, options = {}) {
  const id = productEntityId(product);
  if (!id) {
    throw new Error('product id required');
  }

  const current = Array.isArray(options.currentProducts)
    ? options.currentProducts
    : await readProductsFromBestSource(businessId);

  let matched = false;
  let mergedItem = product;
  const next = (Array.isArray(current) ? current : []).map((p) => {
    const pid = productEntityId(p);
    if (pid && pid === id) {
      matched = true;
      mergedItem = {
        ...p,
        ...product,
        id: p.id ?? product.id,
        productId: p.productId ?? product.productId
      };
      return mergedItem;
    }
    return p;
  });

  if (!matched) {
    if (options.allowInsert === false) {
      throw new Error('product_not_found');
    }
    next.push(product);
    mergedItem = product;
  }

  return { next, mergedItem, id };
}

async function shouldUseSingleRowProductPut() {
  try {
    const { isEntityStoreNoKvBlobEnabled } = await import('../storage/businessPayloadMeta');
    const { isEntityStoreEnabled } = await import('../storage/entityAggregateStore');
    return isEntityStoreEnabled() && isEntityStoreNoKvBlobEnabled();
  } catch {
    return false;
  }
}

export class KvProductsRepository {
  /**
   * @param {string} businessId
   */
  async listProducts(businessId) {
    return readProductsFromBestSource(businessId);
  }

  /**
   * Replace entire products collection (bulk import / multi-delete).
   * @param {string} businessId
   * @param {Array} products
   */
  async replaceProducts(businessId, products, options = {}) {
    const next = Array.isArray(products) ? products : [];
    let prev = [];
    if (!options.fromCloudPull && !options.skipDeleteDiff) {
      try {
        prev = await readProductsFromBestSource(businessId);
      } catch {
        prev = [];
      }
    }
    await queueBusinessPayloadFieldWrite(businessId, 'products', next);
    await refreshProductsReadCache(businessId, next);
    publishDomainEvent(DOMAIN_EVENTS.PRODUCTS_UPDATED, { businessId, products: next });
    if (!options.fromCloudPull) {
      try {
        const { enqueueHybridEntitySync } = await import('../../services/hybridCatalogSync');
        const { enqueueHybridDeletesForRemoved } = await import('../../services/hybridDeleteSync');
        if (!options.skipDeleteDiff) {
          void enqueueHybridDeletesForRemoved(businessId, 'product', prev, next);
        }
        const { listChangedHybridEntities } = await import('../../services/hybridDiff');
        const changed = listChangedHybridEntities('product', prev, next);
        for (const item of changed) {
          void enqueueHybridEntitySync(businessId, 'product', item);
        }
      } catch {
        /* optional */
      }
    }
    return next;
  }

  /**
   * Merge one product by id. Under NO_KV_BLOB + entity store: single-row put (Phase C).
   * Otherwise: full products field write (Phase B).
   *
   * @param {string} businessId
   * @param {object} product
   * @param {{ currentProducts?: Array, allowInsert?: boolean }} [options]
   * @returns {Promise<Array>}
   */
  async upsertProduct(businessId, product, options = {}) {
    if (!businessId) {
      throw new Error('businessId required');
    }
    if (!product || typeof product !== 'object') {
      throw new Error('product required');
    }

    const { next, mergedItem } = await mergeProductIntoCollection(
      businessId,
      product,
      options
    );

    if (await shouldUseSingleRowProductPut()) {
      await queueEntityItemUpsert(businessId, 'products', mergedItem, {
        nextCollection: next
      });
    } else {
      await queueBusinessPayloadFieldWrite(businessId, 'products', next);
    }

    await refreshProductsReadCache(businessId, next);
    publishDomainEvent(DOMAIN_EVENTS.PRODUCTS_UPDATED, { businessId, products: next });
    if (!options.fromCloudPull) {
      try {
        const { enqueueHybridEntitySync } = await import('../../services/hybridCatalogSync');
        const stamped = {
          ...mergedItem,
          updatedAt: mergedItem.updatedAt || new Date().toISOString()
        };
        void enqueueHybridEntitySync(businessId, 'product', stamped);
      } catch {
        /* optional */
      }
    }
    return next;
  }
}
